/**
 * What the model made of one record.
 *
 * Seven modules ask the same question — lead, deal, customer, trial, contact,
 * company, and the board as a whole — and every one of them answers in the
 * shape the server pins down, so they share this panel rather than growing
 * seven of them. A module written next week gets a working screen the day it
 * is added to the builders.
 *
 * Three things, in the order somebody reads them: the verdict, the evidence,
 * and the thing to do. The message comes last because it is only worth
 * reading once the first three have convinced you — and it is never sent from
 * here. It is copied, read, and sent by a person, because a model that can
 * post to a customer without anybody looking is a model that will.
 */
import { useEffect, useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import { insight, AIUnavailable } from '../../services/ai.js';
import Icon from '../../lib/icons.jsx';
import Sheet from '../Sheet.jsx';

/* What each module is called on screen, and the tone its header carries. */
const TITLES = {
  lead: ['Lead intel', 'g-purple'],
  deal: ['Deal coach', 'g-blue'],
  client: ['Account read', 'g-green'],
  trial: ['Trial coach', 'g-amber'],
  contact: ['Contact read', 'g-blue'],
  company: ['Account read', 'g-blue'],
  queue: ['What to do today', 'g-purple']
};

const TONE = { good: 'up', bad: 'down', flat: 'flat' };

/** The dial. Colour is the verdict; the number is only the detail. */
function Score({ score }) {
  const v = Math.max(0, Math.min(100, Number(score.value) || 0));
  const band = v >= 67 ? 'hi' : v >= 34 ? 'mid' : 'lo';
  return (
    <div className={`ai-score ${band}`}>
      <div className="ai-dial" style={{ '--pct': `${v}%` }}>
        <b>{v}</b>
      </div>
      <div className="ai-scoretxt">
        <span className="llabel">{score.label || 'Score'}</span>
        {score.why ? <p>{score.why}</p> : null}
      </div>
    </div>
  );
}

export default function AIInsightSheet({ module, id, subject, onClose }) {
  const { S, toast } = useApp();
  const [out, setOut] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let gone = false;
    setBusy(true);
    setErr('');
    insight(S, module, id)
      .then(r => { if (!gone) setOut(r); })
      .catch(e => {
        if (gone) return;
        /* An AI failure is almost always one of three things — switched off,
           no key, or the minute's budget — and each of those reads better as
           a sentence in the panel than as a toast that vanishes. */
        setErr(e instanceof AIUnavailable ? e.message : (e.message || 'That did not work'));
      })
      .finally(() => { if (!gone) setBusy(false); });
    return () => { gone = true; };
  }, [S, module, id]);

  const [title, tone] = TITLES[module] || ['AI read', 'g-purple'];
  const msg = out && out.message && (out.message.body || '').trim() ? out.message : null;

  const copy = () => {
    const text = [msg.subject, msg.body].filter(Boolean).join('\n\n');
    navigator.clipboard.writeText(text)
      .then(() => { setCopied(true); toast('Copied — paste it where you are sending from'); })
      .catch(() => toast('Could not copy — select the text instead'));
  };

  return (
    <Sheet
      icon="wand"
      tone={tone}
      wide
      title={title}
      badge={out && out.score ? <span className="pj-badge">{`${out.score.value}/100`}</span> : null}
      onClose={onClose}
      closeLabel="Done"
      bodyClass="ai-form"
      hero={(
        <div className="ai-hero">
          <b>{out?.subject || subject || 'This record'}</b>
          {out?.headline ? <p>{out.headline}</p> : null}
        </div>
      )}
    >
      {busy ? (
        <div className="ai-wait">
          <span className="spin" aria-hidden="true" />
          <p>Reading the record…</p>
          <small>Everything on file — what they did, what we did, and what is owed.</small>
        </div>
      ) : err ? (
        <p className="ai-err"><Icon name="alert" />{err}</p>
      ) : !out ? null : (
        <>
          {out.score ? <Score score={out.score} /> : null}

          {(out.sections || []).filter(s => s && (s.points || []).length).map((s, i) => (
            <section key={`${s.title}-${i}`} className={`ai-sec ${TONE[s.tone] || 'flat'}`}>
              <h5 className="lhead">
                <Icon name={s.tone === 'good' ? 'up' : s.tone === 'bad' ? 'alert' : 'list'} />
                {s.title}
              </h5>
              <ul>{(s.points || []).map((p, n) => <li key={n}>{p}</li>)}</ul>
            </section>
          ))}

          {out.next && out.next.action ? (
            <section className="ai-next">
              <span className="llabel">Do this next</span>
              <b>{out.next.action}</b>
              {out.next.why ? <p>{out.next.why}</p> : null}
              {out.next.when ? <span className={`ai-when ${String(out.next.when).replace(/\s+/g, '-')}`}>{out.next.when}</span> : null}
            </section>
          ) : null}

          {msg ? (
            <section className="ai-msg">
              <h5 className="lhead">
                <Icon name={msg.channel === 'whatsapp' ? 'chat' : 'mail'} />
                {msg.channel === 'whatsapp' ? 'Send on WhatsApp' : 'Send by email'}
                <small>a draft — read it before it goes</small>
              </h5>
              {msg.subject ? <div className="ai-subj"><span className="llabel">Subject</span>{msg.subject}</div> : null}
              <p className="ai-body">{msg.body}</p>
              <button type="button" className="btn sm" onClick={copy}>
                <Icon name={copied ? 'check' : 'file'} />{copied ? 'Copied' : 'Copy this'}
              </button>
            </section>
          ) : null}
        </>
      )}
    </Sheet>
  );
}
